/*eslint no-undef: "warn"*/
import {forwardRef, useEffect, useImperativeHandle, useRef} from "react";
import {useDispatch, useSelector} from 'react-redux';
import {findEquidistantPointsAlongRoute} from "../util/gis";
import buffer from '@turf/buffer'
import * as turf from '@turf/helpers'
import {setTrajectoryStore} from "../store/entities/trajectory";
import {parse, stringify} from 'wellknown';
import {findClosestIndex} from "../util/numbers";
import * as dayjs from 'dayjs';
import {useDebouncedEffect} from "./util/UseDebouncedEffect";
import {toast} from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import {captureScreenshot} from "./util/ScreenshotCreator";

const MAX_ROUTE_LENGTH = 55;

const polyline = require("polyline");
let lastZoomTime;
// let originWaypoint;

// Used for when the user is changing the road width
let originalMapOptions = {
  center: null,
  zoom: null,
  mapTypeId: null,
}

const InputMap = forwardRef((props, ref) => {
  // Local constants
  const validRoles = ['owner', 'administrator', 'editor'];

  const dispatch = useDispatch();

  // Grab stuff from the store
  const roadWidth = useSelector(state => state.entities.trajectory.roadWidth);
  const visibleCoordinates = useSelector(state => state.entities.trajectory.visibleCoordinates);
  const hoverMarkerPosition = useSelector(state => state.entities.trajectory.hoverMarkerPosition);
  const hoveredTrajectoryJourneyId = useSelector(state => state.entities.trajectory.hoveredTrajectoryJourneyId);
  const mileMarkerOffset = useSelector(state => state.entities.trajectory.mileMarkerOffset);
  const subtractMileMarkers = useSelector(state => state.entities.trajectory.subtractMileMarkers);
  const distanceBetweenPoints = useSelector(state => state.entities.trajectory.distanceBetweenPoints);
  const isChangingRoadWidth = useSelector(state => state.entities.trajectory.isChangingRoadWidth);
  const role = useSelector(state => state.entities.trajectory.role);
  const waypoints = useSelector(state => state.entities.trajectory.waypoints);
  const slideshowRunning = useSelector(state => state.entities.trajectory.slideshowRunning);
  const routeName = useSelector(state => state.entities.trajectory.routeName);
  const startDate = useSelector(state => state.entities.trajectory.startDate);
  const endDate = useSelector(state => state.entities.trajectory.endDate);
  const mapCenter = useSelector(state => state.entities.trajectory.mapCenter);
  const mapZoom = useSelector(state => state.entities.trajectory.mapZoom);

  if (localStorage.getItem('center') && localStorage.getItem('zoom')) {
    dispatch(setTrajectoryStore({
      mapCenter: JSON.parse(localStorage.getItem('center')),
      mapZoom: parseInt(localStorage.getItem('zoom'))
    }))
    localStorage.removeItem('center');
    localStorage.removeItem('zoom');
  }

  const directionsRenderer = useRef(null);
  const roadWidthPolygon = useRef(null);
  const routes = useRef(null);
  const map = useRef(null);
  const directionsResponse = useRef(null);
  const equidistantRoute = useRef(null);
  const originalBounds = useRef(null);
  const zoomPolyline = useRef(null);
  const hoverMarker = useRef(null);
  const hoveredTrajectoryLine = useRef(null);
  const originWaypoint = useRef(null);
  const routeMarkers = useRef({origin: null, destination: null})
  // const trafficLayer = useRef();

  let hasListener = false;

  useImperativeHandle(ref, () => ({
    getMapCenter() {
      let center = map.current.getCenter();
      return {lat: center.lat(), lng: center.lng()}
    },
    getMapZoom() {
      return map.current.getZoom();
    },
    resetBounds() {
      if (routes) {
        map.current.setBounds(createBounds(routes[0].coordinates))
      }
    }
  }));

  useEffect(() => {
    drawBufferedPolyline()
  }, [roadWidth]);

  useEffect(() => {
    if (map.current) {
      map.current.setCenter(mapCenter);
      map.current.setZoom(mapZoom);
    }
  }, [mapCenter, mapZoom])

  // This gets fired when the user hover overs one of the travel time points
  useDebouncedEffect(() => {
    if (props.interactive) return;
    if (hoveredTrajectoryJourneyId === null) {
      if (hoveredTrajectoryLine.current) {
        hoveredTrajectoryLine.current.setMap(null)
        hoveredTrajectoryLine.current = null;
      }
      return;
    }

    // Create LatLng literals for Google Maps
    let coordinates = polyline.decode(hoveredTrajectoryJourneyId);
    let path = [];
    for (let coordinate of coordinates) {
      coordinate.reverse();
      let latLng = createLatLng(coordinate)
      if (!latLng) return
      path.push(latLng);
    }

    // Just adjust if it's already displayed
    if (hoveredTrajectoryLine.current && hoveredTrajectoryLine.current.getMap()) {
      hoveredTrajectoryLine.current.setPath(path);
    } else {
      hoveredTrajectoryLine.current = new google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: '#d700ff',
        strokeOpacity: .9,
        strokeWeight: 5,
        zIndex: 1000
      });
      hoveredTrajectoryLine.current.setMap(map.current);
    }

    let slideshowSaveScreenshots = localStorage.getItem('slideshowSaveScreenshots') === 'true';
    let slideshowScreenshotFileNameBase = localStorage.getItem('slideshowScreenshotFileName') || 'screenshot';

    if (slideshowRunning && slideshowSaveScreenshots) {
      let fileName;
      if (routeName) {
        fileName = `${slideshowScreenshotFileNameBase}_${routeName.replace(/\s/g, '_')}_${startDate}_${endDate}_${hoveredTrajectoryJourneyId.slice(0, 8)}.png`;
      } else {
        fileName = `${slideshowScreenshotFileNameBase}_${startDate}_${endDate}_${hoveredTrajectoryJourneyId.slice(0, 8)}.png`;
      }
      captureScreenshot(fileName);
    }
  }, [hoveredTrajectoryJourneyId], 50)

  // This creates the zoom polyline and handles the bounds
  useEffect(() => {
    createZoomPolyline(visibleCoordinates);

    if (visibleCoordinates === null || map.current === null)  return;

    // Either zoom into the visibleCoordinates, or just show the original.
    if (visibleCoordinates.length) {
      map.current.fitBounds(createBounds(visibleCoordinates));
    }
    else map.current.fitBounds(originalBounds.current);
  }, [visibleCoordinates])

  useDebouncedEffect(() => updateHoverMarker(), [hoverMarkerPosition], 10)

  useEffect(() => {
    if (!map.current || !equidistantRoute.current || !props.interactive) return;
    if (isChangingRoadWidth) {
      // We've already saved the options and zoomed in, don't do anything.
      if (!originalMapOptions.mapTypeId && equidistantRoute.current.coordinates.length > 5) {
        originalMapOptions = {
          center: map.current.getCenter(),
          zoom: map.current.getZoom(),
          mapTypeId: map.current.getMapTypeId()
        };
        map.current.fitBounds(createBounds(equidistantRoute.current.coordinates.slice(0, 5)));
        if (originalMapOptions.mapTypeId !== google.maps.MapTypeId.SATELLITE) {
          map.current.setMapTypeId(google.maps.MapTypeId.SATELLITE);
        }
      }
    } else {
      map.current.setCenter(originalMapOptions.center);
      map.current.setZoom(originalMapOptions.zoom);
      map.current.setMapTypeId(originalMapOptions.mapTypeId);
      originalMapOptions = {
        center: null,
        zoom: null,
        mapTypeId: null
      }
    }
  }, [isChangingRoadWidth])

  useDebouncedEffect(() => {
    let useDarkMode = (localStorage.getItem('darkMode') === 'true')
    originWaypoint.current = null;
    map.current = new google.maps.Map(document.getElementById(props.id || 'google-map'), {
      center: mapCenter,
      zoom: mapZoom,
      gestureHandling: 'greedy',
      mapId: useDarkMode ? '993352fc1ff79426' : '6f9d616f9683267c',
      backgroundColor: useDarkMode ? '#000' : '#fff',
      tilt: 0
    }, 200);

    // trafficLayer.current = new google.maps.TrafficLayer({ map: map.current });

    handleNewWaypoints();
  }, [])

  useDebouncedEffect(() => {
    handleNewWaypoints();
  }, [waypoints], 200)

  const handleNewWaypoints = () => {
    if (waypoints) {
      // Add the decoded/parsed coordinates into this array
      let waypointCoordinates;

      // Check if it's a wkt linestring
      if (waypoints.match(/linestring/i)) {
        let geoJSON = parse(waypoints);
        if (geoJSON.coordinates.length >= 2) {
          waypointCoordinates = geoJSON.coordinates.map(c => ({lat: c[1], lng: c[0]}));
        } else return;
      } else {
        toast.error("Invalid waypoints, try again.");
        return;
      }

      let directionsService = new google.maps.DirectionsService();
      let request = {
        'origin': waypointCoordinates[0],
        'destination': waypointCoordinates[waypointCoordinates.length - 1],
        'travelMode': google.maps.DirectionsTravelMode.DRIVING,
        drivingOptions: {
          departureTime: dayjs().add(1, 'year').toDate()
        }
      };

      if (waypointCoordinates.length > 2) {
        request.waypoints = [];
        for (let i = 1; i < waypointCoordinates.length - 1; i++) {
          request.waypoints.push({location: waypointCoordinates[i], stopover: false});
        }
      }

      directionsService.route(request, function (response, status) {
        if (status === 'OK') {
          dispatch(setTrajectoryStore({
            freeFlowTravelTime: response.routes[0].legs[0].duration.value
          }))
          renderDirections(response)
          directionsResponse.current = response;
          updateRoute();
        } else {
          toast.error("Routing error, try again!");
        }
      })
    } else {
      // Reset stuff
      directionsResponse.current = null;
      renderDirections(null);
      drawBufferedPolyline(true);
      equidistantRoute.current = null;
      map.current.setCenter(mapCenter);
      map.current.setZoom(mapZoom);
      if (!waypoints) routes.current = null;

      // If these conditions, add a click listener, but remove the old one first
      if (validRoles.includes(role) && !hasListener && props.interactive) {
        if (map.current) google.maps.event.clearListeners(map.current, 'click');
        map.current.addListener('click', e => mapClicked(e));
        hasListener = true;
      }
    }
  }

  const directionsChangedListener = () => {
    if (directionsRenderer.current) {
      directionsResponse.current = directionsRenderer.current.getDirections();
      updateRoute();
    }
  }

  const renderDirections = directionsRes => {
    if (!directionsRes) directionsResponse.current = null;
    if (directionsRenderer.current) {
      directionsRenderer.current.setMap(null);
      google.maps.event.clearListeners(directionsRenderer.current, 'directions_changed');
    }
    if (!directionsRes) {
      if (routeMarkers.current.origin) routeMarkers.current.origin.setMap(null);
      if (routeMarkers.current.destination) routeMarkers.current.destination.setMap(null);
      return;
    }

    directionsRenderer.current = new google.maps.DirectionsRenderer({
      draggable: validRoles.includes(role) && props.interactive,
      suppressMarkers: !props.interactive,
      // markerOptions: {
      //     optimized: true
      // }
    });

    // Draw custom markers so that they work with screenshots, but only on the outer map
    if (!props.interactive) {
      let origin = directionsRes.request.origin.location;
      let destination = directionsRes.request.destination.location;

      if (routeMarkers.current.origin) routeMarkers.current.origin.setMap(null);
      routeMarkers.current.origin = createMarker({lat: origin.lat(), lng: origin.lng()}, '', {
        text: 'A',
        color: 'white'
      })

      if (routeMarkers.current.destination) routeMarkers.current.destination.setMap(null);
      routeMarkers.current.destination = createMarker({
        lat: destination.lat(),
        lng: destination.lng()
      }, '', {text: 'B', color: 'white'})
    }

    if (validRoles.includes(role)) {
      directionsRenderer.current.addListener('directions_changed', directionsChangedListener)
    }
    if (directionsRes) {
      directionsRenderer.current.setDirections(directionsRes);
      directionsRenderer.current.setMap(map.current);
    }
  }

  const mapClicked = (event) => {
    if (!validRoles.includes(role) || !props.interactive) return;
    if (!event.latLng) return
    if (directionsResponse.current) return;
    if (!originWaypoint.current) {
      originWaypoint.current = new google.maps.Marker({
        position: event.latLng,
        map: map.current
      });
    } else {
      originWaypoint.current.setMap(null)
      let directionsService = new google.maps.DirectionsService();
      directionsService.route({
        'origin': originWaypoint.current.getPosition(),
        'destination': event.latLng,
        'travelMode': google.maps.DirectionsTravelMode.DRIVING,
        drivingOptions: {
          departureTime: dayjs().add(1, 'year').toDate()
        }
      }, function (response, status) {
        originWaypoint.current = null;
        if (status === 'OK') {
          renderDirections(response);
          directionsResponse.current = response;
          updateRoute();
        } else {
          toast.error("Routing error, try again!");
        }
        originWaypoint.current = null;
      })
    }
  }

  /**
   * Draws a buffered polyline on the map around the route
   */
  const drawBufferedPolyline = (remove = false) => {
    if (remove && roadWidthPolygon.current) {
      roadWidthPolygon.current.setMap(null);
      roadWidthPolygon.current = null;
      return;
    }
    if (!routes.current || !roadWidth) return;
    for (let route of routes.current) {
      // Road width in kilometers
      let roadWidthKm = turf.convertLength(roadWidth / 2, 'feet', 'kilometers');
      // Make a buffer around it
      let buffered = buffer(route, roadWidthKm)

      // Find all the coordinates of that buffer polygon
      let gMapsCoords = [];
      for (let coordinate of buffered.geometry.coordinates[0]) {
        let latLng = createLatLng(coordinate);
        if (!latLng) return;
        gMapsCoords.push(latLng);
      }

      // Get rid of the old one
      if (roadWidthPolygon.current) roadWidthPolygon.current.setMap(null)

      // Draw the new one
      roadWidthPolygon.current = null;
      roadWidthPolygon.current = new google.maps.Polygon({
        paths: gMapsCoords,
        strokeColor: '#000dff',
        strokeWeight: 2,
        strokeOpacity: .75,
        fillOpacity: .2,
        fillColor: '#00adfd',
      })
      roadWidthPolygon.current.setMap(map.current);
    }
  };

  /**
   * Draws the marker that shows on hover
   */
  const updateHoverMarker = () => {
    let mileMarker = hoverMarkerPosition;

    // Get rid of the old one if it's null
    if (mileMarker === null) {
      if (hoverMarker.current) {
        hoverMarker.current.setMap(null);
        hoverMarker.current = null;
      }
      return;
    }

    if (!equidistantRoute.current) return
    // Find the max mile marker
    let maxMileMarker = Math.max(...(equidistantRoute.current.mileMarkers));

    // Find the index of the coordinate we want to show the marker at
    let index = findClosestIndex(mileMarker, equidistantRoute.current.mileMarkers);
    let coordinate = equidistantRoute.current.coordinates[index];

    // Update the marker
    let latLng = createLatLng(coordinate);
    if (!latLng) return;
    // If it's already there, just update instead of recreating
    if (hoverMarker.current) {
      hoverMarker.current.setPosition(latLng)
    } else {
      hoverMarker.current = new google.maps.Marker({
        position: latLng,
        // Black marker, white outline
        icon: {
          path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
          fillColor: '#000',
          scale: 2,
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
          anchor: {x: 12, y: 24},
        },
      })
      // Show it
      hoverMarker.current.setMap(map.current);
    }
  };

  /**
   * Creates a Google Maps compatible LatLngLiteral from a coordinate array
   * @param coords [lon, lat]
   * @returns {{lng: *, lat: *}|null} Google Maps LatLngLiteral
   */
  const createLatLng = (coords) => {
    if (!coords) return null;
    return {lng: coords[0], lat: coords[1]}
  };

  /**
   * Should be called whenever the route is changed, either from dragging or on page-load.
   */
  const updateRoute = () => {
    if (!directionsResponse.current.routes.length) throw new Error("DirectionsResponse object has no routes!")

    // dispatch(setTrajectoryStore({routeName: directionsResponse.current.routes[0].summary}));

    // Grab the route(s) from the response
    let geoJSONRoutes = [];
    for (let route of directionsResponse.current.routes) {
      let decoded = polyline.decode(route.overview_polyline);
      // These coordinates come flipped around. Flip em to match geoJSON spec
      if (decoded) {
        for (let coordinate of decoded) {
          coordinate.reverse();
        }
        geoJSONRoutes.push({type: "LineString", coordinates: decoded});
      }
    }
    routes.current = geoJSONRoutes;
    // Find the equidistant route
    if (geoJSONRoutes.length) {
      equidistantRoute.current = findEquidistantPointsAlongRoute(geoJSONRoutes[0], distanceBetweenPoints);
    }
    let maxMileMarker = Math.max(...equidistantRoute.current.mileMarkers)
    if (maxMileMarker > MAX_ROUTE_LENGTH) {
      toast.warning(`Route too long, max ${MAX_ROUTE_LENGTH} miles!`);
    }

    // Set the route in the store
    dispatch(setTrajectoryStore({
      route: geoJSONRoutes[0]
    }))

    // Draw the buffered polyline
    drawBufferedPolyline();

    // Make original bounds so we have something to return to
    originalBounds.current = createBounds(geoJSONRoutes[0].coordinates);

    // What we'll turn into WKT
    let routeForWkt = {
      type: "LineString",
      coordinates: []
    }

    // Update the waypoints
    let routeOrigin, routeDestination;

    // Pull out the origin location
    if (directionsResponse.current.request.origin.location) {
      routeOrigin = directionsResponse.current.request.origin.location;
    } else {
      routeOrigin = directionsResponse.current.request.origin;
    }
    routeForWkt.coordinates.push([routeOrigin.lng(), routeOrigin.lat()])

    // Get the list of waypoints
    let routeWaypoints = directionsResponse.current.request.waypoints;
    if (routeWaypoints) {
      for (let waypoint of routeWaypoints) {
        if (waypoint.location.location) {
          routeForWkt.coordinates.push([waypoint.location.location.lng(), waypoint.location.location.lat()]);
        } else {
          routeForWkt.coordinates.push([waypoint.location.lng(), waypoint.location.lat()]);
        }
      }
    }

    // Pull out the destination location
    if (directionsResponse.current.request.destination.location) {
      routeDestination = directionsResponse.current.request.destination.location;
    } else {
      routeDestination = directionsResponse.current.request.destination;
    }
    routeForWkt.coordinates.push([routeDestination.lng(), routeDestination.lat()]);

    let waypointsString = stringify(routeForWkt);
    dispatch(setTrajectoryStore({
      waypoints: waypointsString
    }));
    // if (submit) {
    //     props.submitListener(waypointsString);
    // }
  }

  /**
   * Creates the polyline showing where the graph is zoomed in to
   * @param coordinates The coordinates to show
   */
  const createZoomPolyline = (coordinates) => {
    if (props.interactive) return;
    if (coordinates === null) {
      if (zoomPolyline.current) {
        zoomPolyline.current.setMap(null)
        zoomPolyline.current = null;
      }
      return;
    }

    // Don't refresh too often
    if (performance.now() - lastZoomTime < 100) return;

    // Create LatLng literals for Google Maps
    let path = [];
    for (let coordinate of coordinates) {
      let latLng = createLatLng(coordinate)
      if (!latLng) return
      path.push(latLng);
    }

    // Just adjust if it's already displayed
    if (zoomPolyline.current) {
      zoomPolyline.current.setPath(path);
    } else {
      const useDarkMode = (localStorage.getItem('darkMode') === 'true')
      zoomPolyline.current = new google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: useDarkMode ? '#9eb1fc' : '#0a2c84',
        strokeOpacity: .75,
        strokeWeight: 10,
        zIndex: 1000
      });
      zoomPolyline.current.setMap(map.current);
    }
    lastZoomTime = performance.now();
  }
  //
  // const removeZoomPolyline = () => {
  // }

  const getCorrectedMileMarker = (mileMarker) => {
    if (subtractMileMarkers) {
      return mileMarkerOffset - mileMarker;
    } else return mileMarkerOffset + mileMarker;
  }

  const createBounds = (coordinates) => {
    let bounds = new google.maps.LatLngBounds();
    for (let coordinate of coordinates) {
      let latLng = createLatLng(coordinate);
      if (!latLng) return;
      bounds.extend(latLng)
    }
    return bounds;
  }

  const createMarker = (coords, title = "", label = "") => {
    let position;
    if (coords.lat) position = coords
    else position = createLatLng(coords)
    if (!position) return
    return new google.maps.Marker({
      position,
      map: map.current,
      title,
      label,
      zIndex: 0
    });
  }

  return (
    <div id={props.id || 'google-map'} style={props.style} className={props.className}/>
  )
});

export default InputMap;
